import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useInstallStore } from './install-store';

declare const __APP_COMMIT__: string;

/**
 * Deliver a new build at the next page change, not "eventually".
 *
 * ── WHY ──
 * Fint is one page that never reloads: moving from Invoices to the Inbox is a
 * route change in this tab, not a request for a new app. So a tab left open
 * across a deploy ran the old app for days - the owner kept seeing a bug
 * already fixed on the server, and a search button already replaced, because
 * the "new version" bar is easy to dismiss or miss, and its promise that the
 * next navigation picks the new build up was true only of a full page load.
 *
 * ── WHAT ──
 * Once a new build is known to exist, the NEXT route change is a full reload
 * of that route. Between pages, never in the middle of one, so a half-typed
 * invoice is never thrown away - the moment someone leaves a page is the
 * moment nothing on it is unsaved.
 *
 * A new build is known two ways: the service worker installed one (the
 * existing UPDATE_READY signal), or the server reports a commit this app was
 * not built from (`/v1/health`, asked on load, when the tab comes back into
 * view, and every ten minutes). The second covers a tab no worker controls.
 * Either also raises the update bar, for someone who wants it now.
 */
export function StaleAppReload() {
  const location = useLocation();
  const updateReady = useInstallStore((s) => s.updateReady);
  const path = location.pathname + location.search;
  const previous = useRef(path);

  useEffect(() => {
    const app = typeof __APP_COMMIT__ === 'string' ? __APP_COMMIT__ : '';
    // Production only: under `vite dev` the app is HEAD and the API's marker
    // is whatever dist was last built, so they differ and every click would
    // reload. No commit at all (built outside git) is nothing to compare.
    if (!import.meta.env.PROD || !app) return;
    let stopped = false;
    const check = () => {
      fetch('/v1/health', { cache: 'no-store' })
        .then((r) => (r.ok ? r.json() : null))
        .then((health: { build?: { commit?: string | null } } | null) => {
          const server = health?.build?.commit;
          if (!stopped && server && server !== app) useInstallStore.setState({ updateReady: true });
        })
        .catch(() => undefined);
    };
    check();
    const timer = setInterval(check, 10 * 60_000);
    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      stopped = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  useEffect(() => {
    if (previous.current === path) return;
    previous.current = path;
    if (updateReady) window.location.reload();
  }, [path, updateReady]);

  return null;
}
